import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, TextField, DialogActions, Button, Autocomplete, Chip, Box, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import styles from './styles.module.css';

export default function OrderTypeDialog({ handleClose, handleSuccess, existingNames, charges, initialData }) {
  const { t: tCommon } = useTranslation('common');
  const { t: tManagement } = useTranslation('management');
  
  const formattedExistingNames = useMemo(() => {
    return existingNames.map((name) => name.toLowerCase().trim());
  }, [existingNames]);

  const [data, setData] = useState({
    name: initialData?.name || '',
    selectedCharges: initialData?.charges ? charges.filter(charge => initialData.charges.includes(charge.id)) : [],
  });

  const [error, setError] = useState('');

  const isFormValid = data.name.trim() !== '' && !error;

  const handleNameChange = (event) => {
    let newName = event.target.value;
    
    // Prevent leading spaces
    if (newName.startsWith(' ')) {
      newName = newName.trim();
    }

    setData((prev) => ({ ...prev, name: newName }));
    
    const trimmedNewName = newName.trim().toLowerCase();
    const trimmedInitialName = initialData?.name?.trim()?.toLowerCase();
    
    if (
      trimmedNewName !== trimmedInitialName &&
      formattedExistingNames.includes(trimmedNewName)
    ) {
      setError(tManagement('Order type name already exists'));
    } else {
      setError('');
    }
  };

  const handleBlur = () => {
    setData((prev) => ({ ...prev, name: prev.name.trim() }));
  };

  const handleChargesChange = (event, newValue) => {
    setData((prev) => ({ ...prev, selectedCharges: newValue }));
  };

  const handleSubmit = () => {
    if (isFormValid) {
      handleSuccess({
        name: data.name.trim(),
        charges: data.selectedCharges.map(charge => charge.id),
        oldName: initialData?.name
      });
      handleClose();
    }
  };

  return (
    <div className={styles.popUp}>
      <div className={styles.contentWrapper}>
        <div className={styles.headSec}>
          <h2 className={styles.editTitle}>
            {initialData ? tCommon('Edit Order Type') : tCommon('Add Order Type')}
          </h2>
          <IconButton onClick={handleClose} edge="end"><CloseIcon/></IconButton>
        </div>

        <div className={styles.inputSec}>
          <Grid container className={styles.inputRow} spacing={2} alignItems="center">
            <Grid item xs={12} sm={4} className={styles.inputLabelContainer}>
              <h3 className={styles.inputLabel}>{tCommon('Name')}</h3>
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                variant="outlined"
                size="small"
                fullWidth
                value={data.name}
                onChange={handleNameChange}
                onBlur={handleBlur}
                error={!!error}
                helperText={error}
                autoFocus
              />
            </Grid>
          </Grid>

          <Grid container className={styles.inputRow} spacing={2} alignItems="flex-start" sx={{ mt: 1 }}>
            <Grid item xs={12} sm={4} className={styles.inputLabelContainer}>
              <h3 className={styles.inputLabel}>{tCommon('Charge Groups')}</h3>
            </Grid>
            <Grid item xs={12} sm={8}>
              <Autocomplete
                multiple
                options={charges}
                getOptionLabel={(option) => option.name}
                value={data.selectedCharges}
                onChange={handleChargesChange}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField {...params} variant="outlined" size="small" fullWidth />
                )}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => (
                    <Chip key={option.id} label={option.name} {...getTagProps({ index })} size="small" />
                  ))
                }
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 3 }}>
            <DialogActions>
              <Button variant="contained" color="secondary" size="small" onClick={handleClose}>
                {tCommon('Close')}
              </Button>
              <Button
                variant="contained"
                color="primary"
                size="small"
                sx={{ backgroundColor: '#00a65a', '&:hover': { backgroundColor: '#008d4c' } }}
                onClick={handleSubmit}
                disabled={!isFormValid}
              >
                {initialData ? tCommon('Update') : tCommon('Add')}
              </Button>
            </DialogActions>
          </Box>
        </div>
      </div>
    </div>
  );
}